/* iter170 — Admin Timesheet Edit + Audit sheet.
   Opens from the daily-view entry row (or weekly drilldown). Lets admins with
   `edit_timesheets` correct clock_in/out, break_hours, the linked job, fix
   auto-closed / fallback flags, and view the audit trail. Every save writes
   a row to `timeclock_edit_audit` on the backend with a required reason.

   Props:
     - open
     - onOpenChange(bool)
     - entry: { id, user_name, clock_in, clock_out, break_hours, appointment_label, auto_closed, fallback_clockin }
     - mode: 'edit' | 'create'    — create mode collects user_id + initial times
     - employees: [{id, name}]    — only required for create mode
     - onSaved()                  — refresh parent timesheets after success
*/
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Clock, History, Save, Plus, AlertTriangle } from 'lucide-react';
import { fmtLocalTime } from '../lib/timeFmt';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Convert an ISO datetime (with TZ) to the value an <input type="datetime-local">
// expects: 'YYYY-MM-DDTHH:mm' in the user's local time (which for Nexflow is ET).
const toLocalInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

// Convert a 'YYYY-MM-DDTHH:mm' local input back to an ISO string with offset.
const fromLocalInput = (local) => {
  if (!local) return null;
  // Browser's Date treats the missing TZ as local. toISOString → UTC ISO,
  // which is exactly what the backend wants.
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
};

const fmtAuditChange = (c) => {
  if (!c) return '';
  const before = c.before ?? '—';
  const after = c.after ?? '—';
  if (c.field === 'clock_in' || c.field === 'clock_out') {
    const b = before === '—' ? '—' : fmtLocalTime(before);
    const a = after === '—' ? '—' : fmtLocalTime(after);
    return `${c.field}: ${b} → ${a}`;
  }
  return `${c.field}: ${before} → ${after}`;
};

const AdminTimeclockEditSheet = ({ open, onOpenChange, entry, mode = 'edit', employees = [], onSaved }) => {
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [breakHours, setBreakHours] = useState('0');
  const [appointmentLabel, setAppointmentLabel] = useState('');
  const [reason, setReason] = useState('');
  const [clearAutoClosed, setClearAutoClosed] = useState(false);
  const [clearFallback, setClearFallback] = useState(false);
  const [userId, setUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Seed fields whenever the sheet opens.
  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && entry) {
      setClockIn(toLocalInput(entry.clock_in));
      setClockOut(toLocalInput(entry.clock_out));
      setBreakHours(String(entry.break_hours ?? 0));
      setAppointmentLabel(entry.appointment_label || '');
      setUserId(entry.user_id || '');
      setReason('');
      setClearAutoClosed(false);
      setClearFallback(false);
    } else if (mode === 'create') {
      const base = entry?.prefill_date ? new Date(`${entry.prefill_date}T09:00:00`) : new Date();
      if (!entry?.prefill_date) base.setMinutes(0, 0, 0);
      const later = new Date(base.getTime() + 8 * 60 * 60 * 1000);
      setClockIn(toLocalInput(base.toISOString()));
      setClockOut(toLocalInput(later.toISOString()));
      setBreakHours('0');
      setAppointmentLabel('');
      setUserId(entry?.user_id || '');
      setReason('');
    }
  }, [open, mode, entry]);

  // Load audit trail for edit mode.
  useEffect(() => {
    if (!open || mode !== 'edit' || !entry?.id) {
      setAudit([]);
      return;
    }
    setAuditLoading(true);
    axios.get(`${API}/admin/timeclock/${entry.id}/audit`, { withCredentials: true })
      .then(({ data }) => setAudit(Array.isArray(data) ? data : []))
      .catch(() => setAudit([]))
      .finally(() => setAuditLoading(false));
  }, [open, mode, entry?.id]);

  const employeeOptions = useMemo(() => {
    return (employees || []).map((e) => ({ id: e.id, name: e.name || e.user_name })).filter((e) => e.id);
  }, [employees]);

  const handleSave = async () => {
    if (!reason.trim()) {
      toast.error('Reason is required for every timesheet correction.');
      return;
    }
    if (mode === 'create' && !userId) {
      toast.error('Pick an employee for the new entry.');
      return;
    }
    if (!clockIn) {
      toast.error('Clock-in time is required.');
      return;
    }
    setSaving(true);
    try {
      if (mode === 'edit') {
        const payload = {
          clock_in: fromLocalInput(clockIn),
          clock_out: clockOut ? fromLocalInput(clockOut) : null,
          break_hours: parseFloat(breakHours || '0'),
          appointment_label: appointmentLabel,
          reason: reason.trim(),
          clear_auto_closed: clearAutoClosed,
          clear_fallback_clockin: clearFallback,
        };
        const { data } = await axios.put(`${API}/admin/timeclock/${entry.id}`, payload, { withCredentials: true });
        if (data?.changed === false) {
          toast.message('No changes detected.');
        } else {
          toast.success(`Timesheet corrected — ${data?.fields?.length || 0} field${data?.fields?.length === 1 ? '' : 's'} updated.`);
          onSaved && onSaved();
        }
      } else {
        const payload = {
          user_id: userId,
          clock_in: fromLocalInput(clockIn),
          clock_out: clockOut ? fromLocalInput(clockOut) : null,
          break_hours: parseFloat(breakHours || '0'),
          appointment_label: appointmentLabel,
          reason: reason.trim(),
        };
        await axios.post(`${API}/admin/timeclock`, payload, { withCredentials: true });
        toast.success('Timesheet entry created.');
        onSaved && onSaved();
      }
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : 'Save failed');
    }
    setSaving(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-[#141414] border-l border-[#262626] text-white w-full sm:max-w-xl overflow-y-auto"
        data-testid="admin-timeclock-edit-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#D4AF37]" />
            {mode === 'create' ? 'Add Missing Timesheet Entry' : 'Correct Timesheet Entry'}
          </SheetTitle>
          <SheetDescription className="text-[#A3A3A3]">
            {mode === 'create'
              ? 'Create a new clock-in/out record for an employee. Every entry is audited.'
              : `Editing ${entry?.user_name || 'employee'}'s entry. Every field-level change is captured in the audit trail with your name + reason.`}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-5">
          {mode === 'create' && (
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Employee</Label>
              <Select value={userId} onValueChange={setUserId}>
                <SelectTrigger data-testid="ts-edit-employee" className="bg-[#0A0A0A] border-[#262626] text-white mt-1">
                  <SelectValue placeholder="Pick employee…" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#262626]">
                  {employeeOptions.map((e) => (
                    <SelectItem key={e.id} value={e.id} className="text-white">{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Clock-In</Label>
              <Input
                data-testid="ts-edit-clock-in"
                type="datetime-local"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Clock-Out</Label>
              <Input
                data-testid="ts-edit-clock-out"
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
                placeholder="Leave blank to keep open"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Break Hours</Label>
              <Input
                data-testid="ts-edit-break-hours"
                type="number"
                step="0.25"
                min="0"
                value={breakHours}
                onChange={(e) => setBreakHours(e.target.value)}
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Job / Shift Label</Label>
              <Input
                data-testid="ts-edit-job-label"
                value={appointmentLabel}
                onChange={(e) => setAppointmentLabel(e.target.value)}
                placeholder="e.g. Smith — water heater"
                className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
              />
            </div>
          </div>

          {mode === 'edit' && (entry?.auto_closed || entry?.fallback_clockin) && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded p-3 space-y-2">
              <p className="text-amber-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Flags on this entry
              </p>
              {entry?.auto_closed && (
                <label data-testid="ts-edit-clear-auto-closed" className="flex items-center gap-2 text-sm text-white">
                  <input type="checkbox" checked={clearAutoClosed} onChange={(e) => setClearAutoClosed(e.target.checked)} />
                  Clear the auto-closed flag (you&apos;ve corrected the clock-out manually)
                </label>
              )}
              {entry?.fallback_clockin && (
                <label data-testid="ts-edit-clear-fallback" className="flex items-center gap-2 text-sm text-white">
                  <input type="checkbox" checked={clearFallback} onChange={(e) => setClearFallback(e.target.checked)} />
                  Clear the fallback clock-in flag (a shift was linked or reason no longer applies)
                </label>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Reason for Correction *</Label>
            <Textarea
              data-testid="ts-edit-reason"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Example: Tech forgot to clock out at 5 PM — fixed per text confirmation."
              className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
            />
            <p className="text-[#737373] text-[11px] mt-1">Required — every correction is captured in the audit trail.</p>
          </div>

          <div className="pt-2 flex items-center gap-2">
            <Button
              data-testid="ts-edit-save"
              onClick={handleSave}
              disabled={saving}
              className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold disabled:opacity-50"
            >
              {mode === 'create' ? <Plus className="w-4 h-4 mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              {mode === 'create' ? 'Create Entry' : 'Save Correction'}
            </Button>
            <Button
              type="button"
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="border-[#262626] text-white hover:bg-[#262626]"
            >
              Cancel
            </Button>
          </div>

          {mode === 'edit' && (
            <div className="border-t border-[#262626] pt-4">
              <p className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold flex items-center gap-1.5 mb-2">
                <History className="w-3.5 h-3.5" /> Edit History
              </p>
              {auditLoading && <p className="text-[#A3A3A3] text-xs">Loading audit…</p>}
              {!auditLoading && audit.length === 0 && (
                <p className="text-[#525252] text-xs italic">No prior corrections on this entry.</p>
              )}
              {!auditLoading && audit.length > 0 && (
                <div className="space-y-2" data-testid="ts-edit-audit-list">
                  {audit.map((a) => (
                    <div key={a.id} data-testid={`ts-edit-audit-${a.id}`} className="bg-[#0A0A0A] border border-[#262626] rounded p-2.5">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <p className="text-white text-sm">
                          <span className="font-bold">{a.edited_by_name || 'admin'}</span>{' '}
                          <span className="text-[#A3A3A3] text-xs">· {a.action} · {a.edited_at ? new Date(a.edited_at).toLocaleString() : ''}</span>
                        </p>
                        <span className="text-[10px] uppercase tracking-wider text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-1.5 py-0.5 rounded">
                          Week of {a.payroll_week_start}
                        </span>
                      </div>
                      <p className="text-[#FCA5A5] text-xs italic mt-0.5">Reason: {a.reason}</p>
                      {Array.isArray(a.changes) && a.changes.length > 0 && (
                        <ul className="mt-1 text-xs text-[#C9C9C9] list-disc ml-4 space-y-0.5">
                          {a.changes.map((c, idx) => (
                            <li key={idx}>{fmtAuditChange(c)}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AdminTimeclockEditSheet;
